import * as money from "shared/money"
import merge from "lodash/merge"

export const defaultTheme = {
  "color-1": "006AF9",
  "color-2": "4795FF",
  "color-3": "7BB3FF",
  "color-4": "00439D",
  "color-5": "0057CD"
}

export const stripChars = (s) => s.replace(/[$%,]/g, "")

export const digitsOnly = (s) => s.replace(/\D+/g, "") // only digits remain

// Functions for rounding to decimal places
export const round = (value, decimals = 2) => Number(Math.round(value + "e" + decimals) + "e-" + decimals)

// shared money formatting functions
const format = (num) => {
  num = num ? stripChars(num.toString()) : "0"
  const isNegative = num < 0
  num = Math.abs(num)
  // let sign = (num == (num = Math.abs(num)));
  num = Math.floor(num * 100 + 0.50000000001)
  num = Math.floor(num / 100).toString()
  for (let i = 0; i < Math.floor((num.length - (1 + i)) / 3); i++) {
    num = num.substring(0, num.length - (4 * i + 3)) + "," + num.substring(num.length - (4 * i + 3))
  }
  return isNegative ? "-" + num : num
}
export const fromString = (amt) => {
  return Math.round(parseFloat(parseFloat(money.format(amt).replace(/,/g, "")).toFixed(2)))
}
export const fromInt = (amt) => money.format(amt)
export const formatDollars = (amt) => `$${fromInt(amt)}`
export const fromIntNoCents = (amt) => format(amt)
export const formatDollarsNoCents = (amt) => `$${fromIntNoCents(amt)}`
export const formatPercent = (dec) => `${Math.round(dec * 100)}%`
export const ifNegative = (value) => (value > 0 ? value : 0)

export const dollars = (num) => formatDollarsNoCents(ifNegative(Number(Math.round(num))))

// shared calculator functions and components
export const getLoanAmt = (price, payment) => price - payment

export const getMonthlyRate = (rate) => rate / 12

export const getMonthlyPayment = (loanAmt, years, rate) => (loanAmt * getMonthlyRate(rate)) / (1 - Math.pow(1 / (1 + getMonthlyRate(rate)), years * 12))

export const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const getPayoffMonth = () => months[new Date().getMonth()]
const getPayoffYear = (years) => new Date().getFullYear() + Number(years)

export const getPayoffDate = (years) => `${getPayoffMonth()} ${getPayoffYear(years)}`

// the below calculates the month and year together, which feels more robust
export const getPayoffDateObject = (monthsToPayoff) => {
  const d = new Date()
  d.setMonth(d.getMonth() + monthsToPayoff)
  return {
    month: months[d.getMonth()],
    year: d.getFullYear()
  }
}

export const getScreenWidth = () => window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth

export const xAxisArray = (num) => Array.from(Array(num + 1).keys())

export const mapColors = (colorClaims, colors, key) => {
  return colorClaims
    .map((aClaim, cId) => merge({}, aClaim, {color: colors[cId]}))
    .filter((aClaim) => aClaim.key === key)
    .reduce((c, e) => {
      const n = c.slice()
      n[e.id] = e.color
      return n
    }, [])
}

const stickyIndexClaim = (_indexState, claimWith) => {
  let id = _indexState.findIndex((e) => !e || JSON.stringify(e) === JSON.stringify(claimWith))
  if (id === -1) {
    id = _indexState.length
  }

  const newState = _indexState.slice()
  newState[id] = claimWith || "non-falsy-value"
  return {indexState: newState, id}
}

export const claim = (_indexState, claimWith) => stickyIndexClaim(_indexState, claimWith)

export const sumCosts = (expenses) => {
  return expenses
    .map((e) => e.cost)
    .reduce((total, next) => {
      return (total || 0) + (next || 0)
    }, 0)
}

export const updateRow = (setState, key) => {
  return (data) => {
    const {index, cost, name} = data
    const internalId = data.id
    setState((prevState) => {
      const newState = {}
      newState[key] = prevState[key].slice()
      const p = newState[key][index]

      let newColorId = p.colorId
      if (internalId !== undefined) {
        const {indexState, id} = claim(prevState.colorClaims, {key, id: internalId})
        newState.colorClaims = indexState
        newColorId = id
      }
      newState[key][index] = {
        cost: cost !== undefined ? cost : p.cost,
        name: name !== undefined ? name : p.name,
        colorId: newColorId
      }
      return newState
    })
  }
}

export const addRow = (setState, key) => {
  return (data) => {
    const {name} = data
    const internalId = data.id
    setState((prevState) => {
      const newState = {}
      const {indexState, id} = claim(prevState.colorClaims, {key, id: internalId})
      newState.colorClaims = indexState
      newState[key] = prevState[key].concat([{name, colorId: id}])
      return newState
    })
  }
}

const stickyIndexFree = (_indexState, releasedId) => {
  const newState = _indexState.slice()
  newState[releasedId] = null
  return newState
}

export const free = (_indexState, releaseId) => stickyIndexFree(_indexState, releaseId)

export const removeRow = (setState, key) => {
  return (index) => {
    setState((prevState) => {
      const newState = {
        colorClaims: free(prevState.colorClaims, prevState[key][index].colorId)
      }
      newState[key] = prevState[key].slice()
      newState[key].splice(index, 1)
      return newState
    })
  }
}

export const get72 = (yearsOrRate) => 72 / yearsOrRate

export const genColorsArray = (obj) => Object.keys(obj).map((key) => `#${obj[key]}`)
